import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { cn, fmtHM } from "@/lib/utils";
import { Plus, Pencil, Trash2, TrendingUp, ArrowDownCircle, ArrowUpCircle, Clock, FileText, Link2, X, AlertTriangle, Check, ChevronsUpDown, Layers, Unlink, UtensilsCrossed, Lock, CalendarClock, FileSpreadsheet } from "lucide-react";

const VAT_OPTIONS = [0, 4, 10, 21];
const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const isOverdue = (row, doneStatus) => row.status !== doneStatus && row.due_date && row.due_date < today();
const INV_STATUS = {
  pending_issue: { label: "Pendiente de emisión", badge: "bg-gold/15 text-[#a9741c] border-transparent" },
  pending: { label: "Pendiente de cobro", badge: "bg-muted text-muted-foreground border-transparent" },
  collected: { label: "Cobrada", badge: "bg-primary/12 text-primary border-transparent" },
};
const IVA_PERSONAL = "personal";
const EXP_ESTADO = {
  prevision: { label: "Previsión", badge: "bg-muted text-muted-foreground border-transparent" },
  confirmado_sin_factura: { label: "Confirmado sin factura", badge: "bg-gold/15 text-[#a9741c] border-transparent" },
  factura: { label: "Factura", badge: "bg-navy/10 text-navy border-transparent" },
};
const EXP_TIPO = [
  { value: "dia_5", label: "Día 5" },
  { value: "dia_20", label: "Día 20" },
  { value: "domiciliado", label: "Domiciliado" },
  { value: "tarjetas", label: "Tarjetas" },
  { value: "contrato_laboral", label: "Contrato laboral" },
  { value: "tique_caja", label: "Tique de caja" },
];

function CompanyPicker({ value, valueName, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    let active = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/companies-lookup${q ? `?search=${encodeURIComponent(q)}` : ""}`);
        if (active) setOpts(r.data || []);
      } catch (e) { /* sin permiso o error: se mantiene el cliente por defecto */ }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="invoice-client-trigger">
          <span className="truncate">{valueName || "Selecciona cliente…"}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar empresa…" value={q} onValueChange={setQ} data-testid="invoice-client-search" />
          <CommandList>
            <CommandEmpty>Sin resultados</CommandEmpty>
            <CommandGroup>
              {opts.map((o) => (
                <CommandItem key={o.id} value={o.id} onSelect={() => { onChange(o.id, o.name); setOpen(false); }} data-testid={`invoice-client-opt-${o.id}`}>
                  <Check className={cn("mr-2 h-4 w-4", value === o.id ? "opacity-100" : "opacity-0")} /> {o.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function InvoiceDialog({ open, onOpenChange, projectId, initial, defaultCompany, onSaved }) {
  const editing = !!initial?.id;
  const [f, setF] = useState(initial || {
    concept: "", base_amount: 0, vat_rate: 21, due_date: "", issue_date: "",
    status: "pending_issue", collected_date: "", invoice_number: "", payment_method: "",
    company_id: defaultCompany?.id || null, company_name: defaultCompany?.name || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const total = (Number(f.base_amount) || 0) * (1 + (Number(f.vat_rate) || 0) / 100);

  const save = async () => {
    if (!f.concept.trim()) { toast.error("Indica un concepto"); return; }
    setSaving(true);
    try {
      const payload = {
        concept: f.concept, base_amount: Number(f.base_amount) || 0, vat_rate: Number(f.vat_rate) || 0,
        due_date: f.due_date || null, status: f.status,
        collected_date: f.status === "collected" ? (f.collected_date || today()) : null,
        company_id: f.company_id || null, company_name: f.company_name || null,
        invoice_number: f.invoice_number || null, issue_date: f.issue_date || null,
        payment_method: f.payment_method || null,
      };
      if (editing) await api.patch(`/invoices/${initial.id}`, payload);
      else await api.post(`/projects/${projectId}/invoices`, payload);
      toast.success(editing ? "Factura actualizada" : "Factura prevista añadida");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Editar factura" : "Nueva factura"}</DialogTitle><DialogDescription className="sr-only">Formulario de factura</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5"><Label>Concepto *</Label><Input value={f.concept} onChange={(e) => set("concept", e.target.value)} data-testid="invoice-concept-input" placeholder="Ej: 50% inicio de proyecto" /></div>
          <div className="space-y-1.5"><Label>Cliente</Label><CompanyPicker value={f.company_id} valueName={f.company_name} onChange={(id, name) => setF((p) => ({ ...p, company_id: id, company_name: name }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Base (€)</Label><Input type="number" value={f.base_amount} onChange={(e) => set("base_amount", e.target.value)} data-testid="invoice-base-input" /></div>
            <div className="space-y-1.5">
              <Label>IVA</Label>
              <Select value={String(f.vat_rate)} onValueChange={(v) => set("vat_rate", v)}>
                <SelectTrigger data-testid="invoice-vat-select"><SelectValue /></SelectTrigger>
                <SelectContent>{VAT_OPTIONS.map((v) => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">Total con IVA: <span className="font-semibold text-foreground">{eur(total)}</span></div>
          <div className="space-y-1.5"><Label>Nº de factura (contabilidad)</Label><Input value={f.invoice_number || ""} onChange={(e) => set("invoice_number", e.target.value)} data-testid="invoice-number-input" placeholder="Ej: 2026/123" /></div>
          <div className="space-y-1.5">
            <Label>Estado</Label>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending_issue">Pendiente de emisión</SelectItem>
                <SelectItem value="pending">Pendiente de cobro</SelectItem>
                <SelectItem value="collected">Cobrada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Fecha prevista de emisión</Label><Input type="date" value={f.issue_date || ""} onChange={(e) => set("issue_date", e.target.value)} data-testid="invoice-issue-input" /></div>
            <div className="space-y-1.5"><Label>Fecha prevista de cobro</Label><Input type="date" value={f.due_date || ""} onChange={(e) => set("due_date", e.target.value)} data-testid="invoice-due-input" /></div>
          </div>
          {f.status === "collected" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Fecha de cobro</Label><Input type="date" value={f.collected_date || ""} onChange={(e) => set("collected_date", e.target.value)} data-testid="invoice-collected-input" /></div>
              <div className="space-y-1.5">
                <Label>Método de cobro</Label>
                <Select value={f.payment_method || ""} onValueChange={(v) => set("payment_method", v)}>
                  <SelectTrigger data-testid="invoice-method-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="CPC">CPC</SelectItem><SelectItem value="BIP">BIP</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="invoice-save-btn">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExpenseDialog({ open, onOpenChange, projectId, suppliers, initial, onSaved }) {
  const editing = !!initial?.id;
  const initSupplier = initial?.supplier_id || (initial?.supplier_name ? "__other__" : "none");
  const initIva = initial ? (initial.personal_contratado ? IVA_PERSONAL : String(initial.vat_rate ?? 21)) : "21";
  const [f, setF] = useState(initial
    ? { ...initial, supplier_sel: initSupplier, supplier_name: initial.supplier_name || "",
        unit_price: initial.unit_price != null ? initial.unit_price : (initial.amount || 0),
        units: initial.units != null ? initial.units : 1,
        iva: initIva, tipo_gasto: initial.tipo_gasto || "", estado: initial.estado || "prevision",
        pagado: initial.pagado != null ? initial.pagado : (initial.status === "paid"), paid_date: initial.paid_date || "" }
    : { concept: "", unit_price: 0, units: 1, iva: "21", supplier_sel: "none", supplier_name: "",
        due_date: "", tipo_gasto: "", estado: "prevision", pagado: false, paid_date: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const vatRate = f.iva === IVA_PERSONAL ? 0 : (Number(f.iva) || 0);
  const base = (Number(f.unit_price) || 0) * (Number(f.units) || 0);
  const total = base * (1 + vatRate / 100);

  const save = async () => {
    if (!f.concept.trim()) { toast.error("Indica un concepto"); return; }
    setSaving(true);
    try {
      let supplier_id = null, supplier_name = "";
      if (f.supplier_sel === "__other__") { supplier_name = f.supplier_name || ""; }
      else if (f.supplier_sel !== "none") { supplier_id = f.supplier_sel; supplier_name = (suppliers.find((s) => s.id === f.supplier_sel) || {}).name || ""; }
      const payload = {
        concept: f.concept, unit_price: Number(f.unit_price) || 0, units: Number(f.units) || 0, amount: base,
        vat_rate: vatRate, personal_contratado: f.iva === IVA_PERSONAL,
        tipo_gasto: f.tipo_gasto || null, estado: f.estado,
        supplier_id, supplier_name, due_date: f.due_date || null,
        pagado: !!f.pagado, status: f.pagado ? "paid" : "pending",
        paid_date: f.pagado ? (f.paid_date || today()) : null,
      };
      if (editing) await api.patch(`/expenses/${initial.id}`, payload);
      else await api.post(`/projects/${projectId}/expenses`, payload);
      toast.success(editing ? "Gasto actualizado" : "Gasto previsto añadido");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editing ? "Editar gasto previsto" : "Nuevo gasto previsto"}</DialogTitle><DialogDescription className="sr-only">Formulario de gasto previsto</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5"><Label>Concepto *</Label><Input value={f.concept} onChange={(e) => set("concept", e.target.value)} data-testid="expense-concept-input" placeholder="Ej: Impresión, freelance…" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Importe (€)</Label><Input type="number" value={f.unit_price} onChange={(e) => set("unit_price", e.target.value)} data-testid="expense-amount-input" /></div>
            <div className="space-y-1.5"><Label>Nº unidades</Label><Input type="number" value={f.units} onChange={(e) => set("units", e.target.value)} data-testid="expense-units-input" /></div>
            <div className="space-y-1.5">
              <Label>IVA</Label>
              <Select value={f.iva} onValueChange={(v) => set("iva", v)}>
                <SelectTrigger data-testid="expense-iva-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VAT_OPTIONS.map((v) => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}
                  <SelectItem value={IVA_PERSONAL}>Costes personal contratado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">Total con IVA: <span className="font-semibold text-foreground">{eur(total)}</span></div>
          <div className="space-y-1.5">
            <Label>Proveedor / persona</Label>
            <Select value={f.supplier_sel} onValueChange={(v) => set("supplier_sel", v)}>
              <SelectTrigger data-testid="expense-supplier-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin asignar</SelectItem>
                {(suppliers || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                <SelectItem value="__other__">Otra persona (escribir)…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {f.supplier_sel === "__other__" && <Input value={f.supplier_name} onChange={(e) => set("supplier_name", e.target.value)} placeholder="Nombre de la persona/proveedor" data-testid="expense-supplier-name-input" />}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Fecha prevista de pago</Label><Input type="date" value={f.due_date || ""} onChange={(e) => set("due_date", e.target.value)} data-testid="expense-due-input" /></div>
            <div className="space-y-1.5">
              <Label>Tipo de gasto</Label>
              <Select value={f.tipo_gasto || "none"} onValueChange={(v) => set("tipo_gasto", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="expense-tipo-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {EXP_TIPO.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Estado</Label>
            <Select value={f.estado} onValueChange={(v) => set("estado", v)}>
              <SelectTrigger data-testid="expense-estado-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="prevision">Previsión</SelectItem>
                <SelectItem value="confirmado_sin_factura">Confirmado sin factura</SelectItem>
                <SelectItem value="factura">Factura</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="exp-pagado" checked={f.pagado} onCheckedChange={(v) => set("pagado", !!v)} data-testid="expense-pagado-check" />
            <Label htmlFor="exp-pagado" className="cursor-pointer font-normal">Pagado (ya salió de banco)</Label>
          </div>
          {f.pagado && <div className="space-y-1.5"><Label>Fecha de pago real</Label><Input type="date" value={f.paid_date || ""} onChange={(e) => set("paid_date", e.target.value)} data-testid="expense-paiddate-input" /></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="expense-save-btn">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DealDialog({ open, onOpenChange, projectId, currentId, onSaved }) {
  const { data: deals } = useFetch("/deals");
  const [search, setSearch] = useState("");
  const rows = (deals || []).filter((d) => !search || d.title?.toLowerCase().includes(search.toLowerCase()) || d.company_name?.toLowerCase().includes(search.toLowerCase()));

  const pick = async (deal_id) => {
    try { await api.patch(`/projects/${projectId}/deal`, { deal_id }); toast.success(deal_id ? "Presupuesto asociado" : "Presupuesto desvinculado"); onSaved(); onOpenChange(false); }
    catch (e) { toast.error("Error"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Asociar presupuesto de cliente</DialogTitle><DialogDescription className="sr-only">Buscar y asociar un presupuesto importado</DialogDescription></DialogHeader>
        <Input placeholder="Buscar presupuesto…" value={search} onChange={(e) => setSearch(e.target.value)} className="mb-2" data-testid="deal-picker-search" />
        {rows.length > 100 && <p className="text-xs text-muted-foreground mb-1">Mostrando los primeros 100 de {rows.length}. Usa el buscador para afinar.</p>}
        <div className="max-h-80 overflow-y-auto space-y-1">
          {rows.slice(0, 100).map((d) => (
            <button key={d.id} onClick={() => pick(d.id)} className={cn("w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors flex items-center justify-between gap-3", d.id === currentId && "bg-primary/10")} data-testid={`deal-pick-${d.id}`}>
              <div className="min-w-0"><div className="text-sm font-medium truncate">{d.title}</div><div className="text-xs text-muted-foreground truncate">{d.company_name || "—"}</div></div>
              <span className="text-sm tabular-nums shrink-0">{d.amount != null ? eur(d.amount) : ""}</span>
            </button>
          ))}
          {rows.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Sin resultados.</p>}
        </div>
        {currentId && <DialogFooter><Button variant="outline" className="text-destructive" onClick={() => pick(null)}>Quitar asociación</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

function DiningLink({ projectId, onChange }) {
  const [state, setState] = useState(null);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/dining-link`);
      setState(data); setSelected(data.linked_wave_id || "");
    } catch { setState({ eligible: false }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
  if (!state || !state.eligible) return null;

  const link = async () => {
    if (!selected) return toast.error("Elige una Wave");
    setBusy(true);
    try { await api.post(`/projects/${projectId}/dining-link`, { wave_id: selected }); toast.success("Wave de Dining vinculada"); await load(); onChange && onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo vincular"); } finally { setBusy(false); }
  };
  const unlink = async () => {
    if (!window.confirm("¿Desvincular la Wave? Dejarán de aparecer sus facturas de Dining como gastos.")) return;
    setBusy(true);
    try { await api.delete(`/projects/${projectId}/dining-link`); toast.success("Wave desvinculada"); await load(); onChange && onChange(); }
    catch (e) { toast.error("Error"); } finally { setBusy(false); }
  };

  return (
    <Card className="border-navy/15 shadow-none p-3 flex flex-wrap items-center justify-between gap-3" data-testid="dining-wave-link">
      <div className="flex items-center gap-2 text-sm">
        <UtensilsCrossed className="h-4 w-4 text-navy" />
        <span className="font-medium">Wave de Dining</span>
        <span className="text-muted-foreground hidden sm:inline">· sus facturas aparecen abajo como gastos (solo lectura)</span>
      </div>
      {state.linked_wave ? (
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1"><Layers className="h-3 w-3" /> {state.linked_wave.name}</Badge>
          {state.can_edit && <Button size="sm" variant="ghost" className="h-8 gap-1.5 text-muted-foreground hover:text-destructive" onClick={unlink} disabled={busy} data-testid="dining-unlink-btn"><Unlink className="h-3.5 w-3.5" /> Desvincular</Button>}
        </div>
      ) : state.can_edit ? (
        <div className="flex items-center gap-2">
          <select className="h-8 rounded-md border border-input bg-white px-2 text-sm max-w-[220px]" value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="dining-wave-select">
            <option value="">Selecciona una Wave…</option>
            {(state.waves || []).map((w) => { const taken = w.linked_project_id && w.linked_project_id !== projectId; return <option key={w.id} value={w.id} disabled={taken}>{w.name}{taken ? ` (vinculada a ${w.linked_project_ref || w.linked_project_name})` : ""}</option>; })}
          </select>
          <Button size="sm" className="h-8 gap-1.5" onClick={link} disabled={busy || !selected} data-testid="dining-link-btn"><Link2 className="h-3.5 w-3.5" /> Vincular</Button>
        </div>
      ) : <span className="text-xs text-muted-foreground">Sin Wave vinculada</span>}
    </Card>
  );
}

export default function ProjectFinance({ projectId, ingresosAccess = "none", gastosAccess = "none", onOpenSchedule }) {
  const { data, loading, refetch } = useFetch(`/projects/${projectId}/finance`);
  const { data: suppliers } = useFetch("/suppliers");
  const [invDialog, setInvDialog] = useState(false);
  const [expDialog, setExpDialog] = useState(false);
  const [dealDialog, setDealDialog] = useState(false);
  const [editInv, setEditInv] = useState(null);
  const [editExp, setEditExp] = useState(null);
  const canIng = ingresosAccess !== "none";
  const canGas = gastosAccess !== "none";
  const editIng = ingresosAccess === "edit";
  const editGas = gastosAccess === "edit";
  const [view, setView] = useState(canIng ? "ingresos" : "gastos");

  const delRow = async (kind, id) => {
    if (!window.confirm("¿Eliminar esta línea?")) return;
    try { await api.delete(`/${kind}/${id}`); toast.success("Eliminado"); refetch(); } catch (e) { toast.error("Error"); }
  };
  const toggleCollected = async (inv) => {
    try {
      await api.patch(`/invoices/${inv.id}`, {
        concept: inv.concept, base_amount: inv.base_amount, vat_rate: inv.vat_rate, due_date: inv.due_date,
        company_id: inv.company_id, company_name: inv.company_name, invoice_number: inv.invoice_number,
        issue_date: inv.issue_date, payment_method: inv.payment_method,
        status: inv.status === "collected" ? "pending" : "collected",
        collected_date: inv.status === "collected" ? null : today(),
      });
      refetch();
    } catch (e) { toast.error("Error"); }
  };
  const togglePaid = async (exp) => {
    const nowPaid = !(exp.pagado || exp.status === "paid");
    try { await api.patch(`/expenses/${exp.id}`, {
      concept: exp.concept, unit_price: exp.unit_price, units: exp.units, amount: exp.amount,
      vat_rate: exp.vat_rate, personal_contratado: exp.personal_contratado, tipo_gasto: exp.tipo_gasto, estado: exp.estado,
      supplier_id: exp.supplier_id, supplier_name: exp.supplier_name, due_date: exp.due_date,
      pagado: nowPaid, status: nowPaid ? "paid" : "pending", paid_date: nowPaid ? today() : null,
    }); refetch(); }
    catch (e) { toast.error("Error"); }
  };

  if (loading || !data) return <div className="flex justify-center py-16"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const s = data.summary;
  const invoices = data.invoices || [];
  const expenses = data.expenses || [];
  const deal = data.deal;
  const needsSchedule = invoices.some((i) => i.needs_schedule);

  const CARDS = [
    (canIng && canGas) && { label: "Margen previsto", value: eur(s.margin), sub: `${s.margin_pct}% sobre ingresos (base)`, icon: TrendingUp, accent: s.margin >= 0 ? "text-primary bg-primary/12" : "text-destructive bg-destructive/12" },
    canIng && { label: "Ingresos (base s/IVA)", value: eur(s.income.net), sub: `Cobrado ${eur(s.income.collected)} · Pendiente ${eur(s.income.pending)} (c/IVA)`, icon: ArrowDownCircle, accent: "text-primary bg-primary/12" },
    canGas && { label: "Gastos (base s/IVA)", value: eur(s.expense.net), sub: `Pagado ${eur(s.expense.paid)} · Pendiente ${eur(s.expense.pending)} (c/IVA)`, icon: ArrowUpCircle, accent: "text-gold bg-gold/15" },
    { label: "Horas imputadas", value: fmtHM(s.hours_spent), sub: s.hours_source === "teamleader" ? "según Teamleader · coste/hora pendiente" : "coste/hora pendiente de definir", icon: Clock, accent: "text-chart-4 bg-chart-4/12" },
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      {needsSchedule && canIng && (
        <Card className="border-gold/40 bg-gold/[0.07] shadow-none p-4 flex flex-wrap items-center justify-between gap-3" data-testid="finance-needs-schedule-banner">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-md bg-gold/20 text-[#a9741c] flex items-center justify-center shrink-0"><AlertTriangle className="h-4 w-4" /></div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#a9741c]">Pendiente cronograma de pago</div>
              <div className="text-xs text-muted-foreground">Hay una factura provisional por el total del presupuesto. Define el cronograma de pago para repartirla en facturas con sus fechas reales de cobro.</div>
            </div>
          </div>
          {onOpenSchedule && <Button size="sm" variant="outline" className="gap-1.5 shrink-0 border-gold/50 text-[#a9741c] hover:bg-gold/10" onClick={onOpenSchedule} data-testid="finance-go-schedule-btn"><CalendarClock className="h-4 w-4" /> Ir al cronograma</Button>}
        </Card>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {CARDS.map((c, i) => (
          <Card key={i} className="border-border shadow-none p-4" data-testid={`finance-card-${i}`}>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2"><div className={cn("h-7 w-7 rounded-md flex items-center justify-center", c.accent)}><c.icon className="h-4 w-4" /></div>{c.label}</div>
            <div className="font-heading font-bold text-xl tabular-nums leading-none">{c.value}</div>
            <div className="text-[11px] text-muted-foreground mt-1.5">{c.sub}</div>
          </Card>
        ))}
      </div>

      {/* Presupuesto de cliente */}
      {canIng && (
      <Card className="border-border shadow-none p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-md bg-navy/8 text-navy flex items-center justify-center shrink-0"><FileText className="h-4 w-4" /></div>
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground">Propuesta / oportunidad asociada</div>
            {deal ? <div className="text-sm font-medium truncate">{deal.title} {deal.amount != null && <span className="text-muted-foreground">· {eur(deal.amount)}</span>} <Badge variant="outline" className="ml-1 border-transparent bg-muted text-xs">{deal.status_label}</Badge></div>
              : <div className="text-sm text-muted-foreground">Ninguno</div>}
          </div>
        </div>
        {editIng && <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={() => setDealDialog(true)} data-testid="link-deal-btn"><Link2 className="h-4 w-4" /> {deal ? "Cambiar" : "Asociar"}</Button>}
      </Card>
      )}

      {/* Alternador Ingresos / Gastos */}
      <div className="flex items-center gap-1 p-1 bg-muted rounded-lg w-fit" data-testid="finance-view-toggle">
        {canIng && <button onClick={() => setView("ingresos")} data-testid="finance-view-ingresos"
          className={cn("px-4 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors duration-200", view === "ingresos" ? "bg-white shadow-sm text-primary" : "text-muted-foreground hover:text-foreground")}>
          <ArrowDownCircle className="h-4 w-4" /> Ingresos <span className="text-xs opacity-70 tabular-nums">({invoices.length})</span>
        </button>}
        {canGas && <button onClick={() => setView("gastos")} data-testid="finance-view-gastos"
          className={cn("px-4 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors duration-200", view === "gastos" ? "bg-white shadow-sm text-[#a9741c]" : "text-muted-foreground hover:text-foreground")}>
          <ArrowUpCircle className="h-4 w-4" /> Gastos <span className="text-xs opacity-70 tabular-nums">({expenses.length})</span>
        </button>}
      </div>

      {/* Ingresos */}
      {canIng && view === "ingresos" && (
      <Card className="border-border shadow-none overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-heading font-bold">Ingresos previstos · Facturas a emitir</h3>
          {editIng && <Button size="sm" className="gap-1.5" onClick={() => { setEditInv(null); setInvDialog(true); }} data-testid="add-invoice-btn"><Plus className="h-4 w-4" /> Añadir factura</Button>}
        </div>
        {invoices.length === 0 ? <p className="text-sm text-muted-foreground text-center py-10">Sin facturas previstas todavía.</p> : (
          <Table>
            <TableHeader><TableRow><TableHead>Concepto</TableHead><TableHead className="text-right">Base</TableHead><TableHead>IVA</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Cobro previsto</TableHead><TableHead>Estado</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {invoices.map((i) => (
                <TableRow key={i.id} data-testid={`invoice-row-${i.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {i.concept}
                      {i.source === "external_payment" && <Badge className="bg-primary/10 text-primary border-transparent gap-1 text-[10px] py-0"><Link2 className="h-2.5 w-2.5" /> Externo</Badge>}
                      {i.needs_schedule && <Badge className="bg-gold/15 text-[#a9741c] border-transparent gap-1 text-[10px] py-0"><AlertTriangle className="h-2.5 w-2.5" /> Pendiente cronograma</Badge>}
                    </div>
                    {(i.company_name || i.invoice_number) && (
                      <div className="text-xs text-muted-foreground font-normal">
                        {i.company_name}{i.company_name && i.invoice_number ? " · " : ""}{i.invoice_number ? `Nº ${i.invoice_number}` : ""}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{eur(i.base_amount)}</TableCell>
                  <TableCell className="text-muted-foreground">{i.vat_rate}%</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{eur(i.total)}</TableCell>
                  <TableCell className={cn("text-sm", isOverdue(i, "collected") && "text-destructive font-medium")}>
                    {isOverdue(i, "collected") && <AlertTriangle className="h-3 w-3 inline mr-1" />}{fmtDate(i.due_date)}
                  </TableCell>
                  <TableCell>
                    <Badge className={(INV_STATUS[i.status] || INV_STATUS.pending).badge} data-testid={`invoice-status-${i.id}`}>
                      {(INV_STATUS[i.status] || INV_STATUS.pending).label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {i.read_only ? (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title="Procedente de Pagos externos (solo lectura)"><Lock className="h-3 w-3" /></span>
                    ) : editIng && <>
                      <Button variant="ghost" size="icon" className="h-8 w-8" title={i.status === "collected" ? "Marcar pendiente" : "Marcar cobrada"} onClick={() => toggleCollected(i)} data-testid={`invoice-toggle-${i.id}`}><Check className={cn("h-4 w-4", i.status === "collected" ? "text-primary" : "text-muted-foreground")} /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditInv(i); setInvDialog(true); }} data-testid={`invoice-edit-${i.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => delRow("invoices", i.id)} data-testid={`invoice-delete-${i.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      )}

      {/* Gastos */}
      {canGas && view === "gastos" && (
      <div className="space-y-4">
      <DiningLink projectId={projectId} onChange={refetch} />
      <Card className="border-border shadow-none overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-heading font-bold">Gastos previstos</h3>
          {editGas && <Button size="sm" className="gap-1.5" onClick={() => { setEditExp(null); setExpDialog(true); }} data-testid="add-expense-btn"><Plus className="h-4 w-4" /> Añadir gasto</Button>}
        </div>
        {expenses.length === 0 ? <p className="text-sm text-muted-foreground text-center py-10">Sin gastos previstos todavía.</p> : (
          <Table>
            <TableHeader><TableRow><TableHead>Concepto</TableHead><TableHead>Proveedor/persona</TableHead><TableHead className="text-right">Importe</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Pago previsto</TableHead><TableHead>Estado</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {expenses.map((e) => (
                <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {e.concept}
                      {e.source === "dining" && <Badge className="bg-navy/10 text-navy border-transparent gap-1 text-[10px] py-0"><UtensilsCrossed className="h-2.5 w-2.5" /> Dining</Badge>}
                      {e.source === "external_payment" && <Badge className="bg-primary/10 text-primary border-transparent gap-1 text-[10px] py-0"><Link2 className="h-2.5 w-2.5" /> Externo</Badge>}
                      {e.source === "simple_budget" && <Badge className="bg-gold/15 text-[#a9741c] border-transparent gap-1 text-[10px] py-0"><FileSpreadsheet className="h-2.5 w-2.5" /> Presupuesto</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{e.supplier_name || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{eur(e.amount)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{eur(e.total)}</TableCell>
                  <TableCell className={cn("text-sm", isOverdue(e, "paid") && "text-destructive font-medium")}>
                    {isOverdue(e, "paid") && <AlertTriangle className="h-3 w-3 inline mr-1" />}{fmtDate(e.due_date)}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col items-start gap-1">
                      <Badge className={(EXP_ESTADO[e.estado] || EXP_ESTADO.prevision).badge} data-testid={`expense-estado-${e.id}`}>{(EXP_ESTADO[e.estado] || EXP_ESTADO.prevision).label}</Badge>
                      {(e.pagado || e.status === "paid") && <Badge className="bg-primary/12 text-primary border-transparent">Pagado</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {e.read_only ? (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title="Procedente de Dining (solo lectura)"><Lock className="h-3 w-3" /></span>
                    ) : editGas && <>
                      <Button variant="ghost" size="icon" className="h-8 w-8" title={(e.pagado || e.status === "paid") ? "Marcar pendiente" : "Marcar pagado"} onClick={() => togglePaid(e)} data-testid={`expense-toggle-${e.id}`}><Check className={cn("h-4 w-4", (e.pagado || e.status === "paid") ? "text-primary" : "text-muted-foreground")} /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditExp(e); setExpDialog(true); }} data-testid={`expense-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => delRow("expenses", e.id)} data-testid={`expense-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      </div>
      )}

      {invDialog && <InvoiceDialog open={invDialog} onOpenChange={setInvDialog} projectId={projectId} initial={editInv} defaultCompany={data.company} onSaved={refetch} />}
      {expDialog && <ExpenseDialog open={expDialog} onOpenChange={setExpDialog} projectId={projectId} suppliers={suppliers || []} initial={editExp} onSaved={refetch} />}
      {dealDialog && <DealDialog open={dealDialog} onOpenChange={setDealDialog} projectId={projectId} currentId={deal?.id} onSaved={refetch} />}
    </div>
  );
}
